
import React, { useState, useEffect } from 'react';
import { storage } from '../lib/storage';
import { financeService } from '../services/financeService';
import { Order, PayoutRequest, PayoutMethod, Store } from '../types';
import { 
  CreditCard, 
  Wallet, 
  ArrowUpRight, 
  ArrowDownLeft, 
  Download, 
  CheckCircle, 
  Clock, 
  ChevronRight,
  TrendingUp,
  FileText,
  AlertCircle,
  Loader2,
  RefreshCw,
  Plus,
  Building2,
  Building,
  X,
  Smartphone,
  Check,
  ShieldCheck,
  Zap,
  Banknote
} from 'lucide-react';

interface AdminFinancesProps {
  storeId: string;
}

const AdminFinances: React.FC<AdminFinancesProps> = ({ storeId }) => {
  const [balances, setBalances] = useState(financeService.calculateBalances(storeId));
  const [payoutRequests, setPayoutRequests] = useState<PayoutRequest[]>([]);
  const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);
  const [isBankModalOpen, setIsBankModalOpen] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [isSettling, setIsSettling] = useState(false);
  const [store, setStore] = useState<Store>(storage.getStoreConfig(storeId));

  const [bankFormData, setBankFormData] = useState<PayoutMethod>({
    type: 'BANK',
    accountName: store.payoutMethod?.accountName || '',
    accountNumber: store.payoutMethod?.accountNumber || '',
    routingNumber: store.payoutMethod?.routingNumber || '',
    bankName: store.payoutMethod?.bankName || '',
  });

  useEffect(() => {
    refreshData();
  }, [storeId]);

  const refreshData = () => {
    setBalances(financeService.calculateBalances(storeId));
    setPayoutRequests(storage.getPayoutRequests().filter(p => p.storeId === storeId));
    setStore(storage.getStoreConfig(storeId));
  };

  const handleRequestPayout = async () => {
    const amount = parseFloat(payoutAmount);
    if (!amount || amount <= 0) return alert("Enter a valid amount.");
    
    setIsRequesting(true);
    try {
      await financeService.requestPayout(storeId, amount);
      setPayoutAmount('');
      setIsWithdrawModalOpen(false);
      refreshData();
    } catch (err: any) {
      alert(err.message);
    } finally {
      setIsRequesting(false);
    }
  };

  const handleSaveBankDetails = () => {
    const updatedStore = { ...store, payoutMethod: bankFormData };
    storage.saveStoreConfig(updatedStore);
    setStore(updatedStore);
    setIsBankModalOpen(false);
    alert("Payout method updated successfully!");
  };

  const handleManualSettlement = () => {
    setIsSettling(true);
    setTimeout(() => {
      const count = financeService.triggerSettlements();
      refreshData();
      setIsSettling(false);
      if (count > 0) alert(`${count} orders settled!`);
    }, 1500);
  };

  return (
    <div className="space-y-10 animate-in slide-in-from-right-10 duration-700 pb-20">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div>
           <h1 className="text-3xl font-black text-gray-900 tracking-tight">Wallet & Payouts</h1>
           <p className="text-gray-400 text-sm font-bold uppercase tracking-widest">Real-time Financial Operations</p>
        </div>
        <div className="flex gap-4">
          <button 
            onClick={handleManualSettlement}
            disabled={isSettling}
            className="flex items-center gap-2 bg-blue-50 text-blue-600 px-6 py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest border border-blue-100 hover:bg-blue-100 transition-all disabled:opacity-50"
          >
            {isSettling ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Sync Settlements
          </button>
          <button className="flex items-center gap-3 bg-gray-900 text-white px-8 py-4 rounded-2xl font-black shadow-xl hover:bg-black transition-all active:scale-95 group">
             <Download className="h-5 w-5 group-hover:translate-y-1 transition-transform" /> 
             <span className="text-[10px] uppercase tracking-widest">Reports</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
         {/* Main Wallet Card */}
         <div className="lg:col-span-2 bg-gradient-to-br from-gray-900 to-blue-900 p-10 rounded-[40px] text-white shadow-2xl relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-12 opacity-5 group-hover:scale-110 transition-transform">
               <Wallet className="h-40 w-40" />
            </div>
            <div className="relative h-full flex flex-col justify-between">
               <div className="flex justify-between items-start">
                  <div className="w-16 h-16 bg-white/10 backdrop-blur-xl rounded-[1.5rem] flex items-center justify-center border border-white/20">
                     <TrendingUp className="h-8 w-8 text-blue-400" />
                  </div>
                  <div className="text-right">
                     <span className="text-[10px] font-black px-4 py-2 bg-emerald-500 rounded-full shadow-lg shadow-emerald-500/20">READY TO WITHDRAW</span>
                  </div>
               </div>
               
               <div className="mt-12 mb-12">
                  <p className="text-blue-200 text-[10px] font-black uppercase tracking-widest mb-2 opacity-70">Settled Funds Balance</p>
                  <h3 className="text-6xl font-black tracking-tighter leading-none">${balances.availableBalance.toLocaleString()}</h3>
                  <div className="flex items-center gap-4 mt-4">
                     <div className="flex items-center gap-2 text-emerald-400 text-xs font-bold">
                        <CheckCircle className="h-4 w-4" /> Instant Payout Active
                     </div>
                     <div className="h-1 w-1 bg-white/20 rounded-full"></div>
                     <div className="text-blue-300 text-xs font-medium">Auto-Settlement {storage.getPlatformConfig().settlementDays}D</div>
                  </div>
               </div>

               <div className="flex flex-col sm:flex-row gap-4">
                  <button 
                    onClick={() => setIsWithdrawModalOpen(true)}
                    className="flex-1 bg-blue-600 hover:bg-blue-500 text-white py-5 rounded-2xl font-black text-xs uppercase tracking-widest shadow-2xl shadow-blue-600/50 transition-all active:scale-95"
                  >
                     Initiate Withdrawal
                  </button>
                  <button 
                    onClick={() => setIsBankModalOpen(true)}
                    className="flex-1 bg-white/10 backdrop-blur border border-white/20 hover:bg-white/20 text-white py-5 rounded-2xl font-black text-xs uppercase tracking-widest transition-all"
                  >
                     {store.payoutMethod ? 'Update Payout Details' : 'Link Bank Account'}
                  </button>
               </div>
            </div>
         </div>

         {/* Side Stats */}
         <div className="space-y-8">
            <div className="bg-white p-10 rounded-[40px] border border-gray-100 shadow-xl shadow-gray-100/30 flex flex-col justify-between">
               <div className="space-y-4">
                  <div className="flex justify-between items-center">
                     <div className="p-4 bg-orange-50 text-orange-600 rounded-2xl">
                        <Clock className="h-6 w-6" />
                     </div>
                     <span className="text-[10px] font-black text-orange-600 bg-orange-50 px-3 py-1 rounded-full uppercase">Locked</span>
                  </div>
                  <div>
                     <p className="text-gray-400 text-[10px] font-black uppercase tracking-widest mb-1">Transit Funds</p>
                     <h3 className="text-3xl font-black text-gray-900 tracking-tighter">${balances.pendingBalance.toLocaleString()}</h3>
                  </div>
               </div>
            </div>

            <div className="bg-emerald-50 p-10 rounded-[40px] border border-emerald-100 shadow-xl shadow-emerald-50/50 flex flex-col justify-between relative overflow-hidden">
               <div className="absolute -right-4 -bottom-4 opacity-5"><TrendingUp className="h-24 w-24" /></div>
               <div className="space-y-4">
                  <div className="flex justify-between items-center">
                     <div className="p-4 bg-emerald-600 text-white rounded-2xl">
                        <CheckCircle className="h-6 w-6" />
                     </div>
                  </div>
                  <div>
                     <p className="text-emerald-700/50 text-[10px] font-black uppercase tracking-widest mb-1">Net Withdrawn</p>
                     <h3 className="text-3xl font-black text-emerald-900 tracking-tighter">${balances.totalWithdrawn.toLocaleString()}</h3>
                  </div>
               </div>
            </div>
         </div>
      </div>

      {/* Payout History Section */}
      <div className="bg-white rounded-[40px] border border-gray-100 shadow-xl overflow-hidden">
         <div className="p-10 border-b border-gray-50 flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3">
               <Banknote className="h-6 w-6 text-blue-600" />
               <h3 className="text-2xl font-black text-gray-900 tracking-tight">Withdrawal Ledger</h3>
            </div>
         </div>
         <div className="overflow-x-auto">
            <table className="w-full text-left">
               <thead className="bg-gray-50/50 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] border-b border-gray-50">
                  <tr>
                     <th className="px-10 py-6">Transaction ID</th>
                     <th className="px-10 py-6">Timeline</th>
                     <th className="px-10 py-6">Method</th>
                     <th className="px-10 py-6">Status</th>
                     <th className="px-10 py-6 text-right">Amount</th>
                  </tr>
               </thead>
               <tbody className="divide-y divide-gray-50">
                  {payoutRequests.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-10 py-32 text-center text-gray-400 font-bold uppercase tracking-widest text-xs opacity-50">No transaction logs available.</td>
                    </tr>
                  ) : (
                    payoutRequests.map((req) => (
                      <tr key={req.id} className="group hover:bg-gray-50/30 transition-all">
                        <td className="px-10 py-8 font-black text-sm text-gray-900">{req.id}</td>
                        <td className="px-10 py-8">
                           <p className="text-xs font-bold text-gray-500">{new Date(req.requestedAt).toLocaleDateString()}</p>
                           <p className="text-[10px] text-gray-400 font-medium">Batch Processed</p>
                        </td>
                        <td className="px-10 py-8">
                           {req.payoutMethodSnapshot ? (
                             <div className="flex items-center gap-2">
                                <Building2 className="h-4 w-4 text-gray-400" />
                                <span className="text-[10px] font-black text-gray-900 uppercase">...{req.payoutMethodSnapshot.accountNumber?.slice(-4)}</span>
                             </div>
                           ) : <span className="text-[10px] font-bold text-gray-300">N/A</span>}
                        </td>
                        <td className="px-10 py-8">
                           <span className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest border ${
                              req.status === 'PAID' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 
                              req.status === 'PENDING' ? 'bg-orange-50 text-orange-600 border-orange-100' : 
                              'bg-red-50 text-red-600 border-red-100'
                           }`}>
                              {req.status}
                           </span>
                        </td>
                        <td className="px-10 py-8 text-right">
                           <p className="text-lg font-black text-gray-900">${req.amount.toLocaleString()}</p>
                        </td>
                      </tr>
                    ))
                  )}
               </tbody>
            </table>
         </div>
      </div>

      {/* Withdraw Modal */}
      {isWithdrawModalOpen && (
        <div className="fixed inset-0 z-[150] bg-gray-950/80 backdrop-blur-xl flex items-center justify-center p-4">
           <div className="bg-white w-full max-w-lg rounded-[40px] shadow-2xl overflow-hidden animate-in zoom-in duration-300">
              <div className="p-8 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                 <div className="flex items-center gap-3">
                    <Wallet className="h-6 w-6 text-blue-600" />
                    <h3 className="text-xl font-black text-gray-900 tracking-tight">Withdraw Funds</h3>
                 </div>
                 <button onClick={() => setIsWithdrawModalOpen(false)} className="p-2 hover:bg-gray-100 rounded-full transition-all">
                    <X className="h-6 w-6 text-gray-400" />
                 </button>
              </div>
              <div className="p-10 space-y-10">
                 {/* Balance Info */}
                 <div className="bg-blue-50 p-6 rounded-[2rem] border border-blue-100 flex items-center justify-between">
                    <div>
                       <p className="text-[10px] font-black text-blue-600 uppercase tracking-widest">Available to payout</p>
                       <h4 className="text-3xl font-black text-gray-900 tracking-tight">${balances.availableBalance.toLocaleString()}</h4>
                    </div>
                    <Zap className="h-10 w-10 text-blue-200" />
                 </div>

                 {store.payoutMethod ? (
                    <div className="space-y-8">
                       <div className="space-y-2">
                          <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Amount to Withdraw ($)</label>
                          <input 
                            type="number" 
                            max={balances.availableBalance}
                            value={payoutAmount}
                            onChange={(e) => setPayoutAmount(e.target.value)}
                            placeholder="0.00"
                            className="w-full px-6 py-5 bg-gray-50 border-none rounded-2xl font-black text-2xl focus:ring-2 focus:ring-blue-600"
                          />
                       </div>

                       <div className="p-6 bg-gray-50 rounded-3xl border border-gray-100 flex items-center justify-between">
                          <div className="flex items-center gap-4">
                             <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center text-blue-600">
                                <Building2 className="h-6 w-6" />
                             </div>
                             <div>
                                <p className="text-xs font-black text-gray-900 uppercase tracking-widest">{store.payoutMethod.bankName}</p>
                                <p className="text-[10px] text-gray-400 font-bold">Ends in {store.payoutMethod.accountNumber?.slice(-4)}</p>
                             </div>
                          </div>
                          <CheckCircle className="h-6 w-6 text-emerald-500" />
                       </div>

                       <button 
                         onClick={handleRequestPayout}
                         disabled={isRequesting || !payoutAmount || parseFloat(payoutAmount) <= 0 || parseFloat(payoutAmount) > balances.availableBalance}
                         className="w-full py-6 rounded-3xl bg-blue-600 hover:bg-blue-700 text-white font-black text-lg shadow-2xl shadow-blue-100 active:scale-95 transition-all disabled:opacity-50"
                       >
                          {isRequesting ? <Loader2 className="h-6 w-6 animate-spin mx-auto" /> : `Confirm Withdrawal`}
                       </button>
                    </div>
                 ) : (
                    <div className="text-center py-10 space-y-6">
                       <div className="w-20 h-20 bg-orange-50 text-orange-600 rounded-full mx-auto flex items-center justify-center">
                          <AlertCircle className="h-10 w-10" />
                       </div>
                       <div className="space-y-2">
                          <h4 className="font-black text-gray-900 text-xl">Payout Method Missing</h4>
                          <p className="text-gray-400 text-sm font-medium">Please link your bank account details before initiating a withdrawal request.</p>
                       </div>
                       <button 
                         onClick={() => { setIsWithdrawModalOpen(false); setIsBankModalOpen(true); }}
                         className="w-full py-5 rounded-3xl bg-gray-900 text-white font-black text-xs uppercase tracking-widest shadow-xl"
                       >
                          Link Bank Account Now
                       </button>
                    </div>
                 )}
              </div>
           </div>
        </div>
      )}

      {/* Bank Details Modal */}
      {isBankModalOpen && (
        <div className="fixed inset-0 z-[160] bg-gray-950/80 backdrop-blur-xl flex items-center justify-center p-4">
           <div className="bg-white w-full max-w-xl rounded-[40px] shadow-2xl overflow-hidden animate-in slide-in-from-bottom-10 duration-500">
              <div className="p-8 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                 <div className="flex items-center gap-3">
                    <Building className="h-6 w-6 text-blue-600" />
                    <h3 className="text-xl font-black text-gray-900 tracking-tight">Payout Configuration</h3>
                 </div>
                 <button onClick={() => setIsBankModalOpen(false)} className="p-2 hover:bg-gray-100 rounded-full transition-all">
                    <X className="h-6 w-6 text-gray-400" />
                 </button>
              </div>
              <div className="p-10 space-y-8">
                 <div className="grid grid-cols-2 gap-4 p-1 bg-gray-100 rounded-2xl mb-4">
                    <button onClick={() => setBankFormData({...bankFormData, type: 'BANK'})} className={`py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${bankFormData.type === 'BANK' ? 'bg-white text-gray-900 shadow-md' : 'text-gray-400'}`}>Bank Transfer</button>
                    <button onClick={() => setBankFormData({...bankFormData, type: 'UPI'})} className={`py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${bankFormData.type === 'UPI' ? 'bg-white text-gray-900 shadow-md' : 'text-gray-400'}`}>UPI / Digital</button>
                 </div>

                 {bankFormData.type === 'BANK' ? (
                   <div className="space-y-6">
                      <div className="space-y-2">
                         <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Account Holder Name</label>
                         <input 
                           type="text" 
                           value={bankFormData.accountName}
                           onChange={e => setBankFormData({...bankFormData, accountName: e.target.value})}
                           placeholder="John Doe" 
                           className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl font-bold" 
                         />
                      </div>
                      <div className="space-y-2">
                         <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Bank Name</label>
                         <input 
                           type="text" 
                           value={bankFormData.bankName}
                           onChange={e => setBankFormData({...bankFormData, bankName: e.target.value})}
                           placeholder="HDFC Bank, HSBC, etc." 
                           className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl font-bold" 
                         />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                         <div className="space-y-2">
                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Account Number</label>
                            <input 
                              type="text" 
                              value={bankFormData.accountNumber}
                              onChange={e => setBankFormData({...bankFormData, accountNumber: e.target.value})}
                              placeholder="0000 0000 0000" 
                              className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl font-bold" 
                            />
                         </div>
                         <div className="space-y-2">
                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">IFSC / Routing Number</label>
                            <input 
                              type="text" 
                              value={bankFormData.routingNumber}
                              onChange={e => setBankFormData({...bankFormData, routingNumber: e.target.value})}
                              placeholder="HDFC0001234" 
                              className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl font-bold uppercase" 
                            />
                         </div>
                      </div>
                   </div>
                 ) : (
                   <div className="space-y-6">
                      <div className="space-y-2">
                         <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">UPI ID / Mobile Number</label>
                         <div className="relative">
                            <Smartphone className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-300" />
                            <input 
                              type="text" 
                              value={bankFormData.upiId}
                              onChange={e => setBankFormData({...bankFormData, upiId: e.target.value})}
                              placeholder="johndoe@okaxis" 
                              className="w-full pl-12 pr-6 py-4 bg-gray-50 border-none rounded-2xl font-bold" 
                            />
                         </div>
                      </div>
                   </div>
                 )}

                 <div className="p-6 bg-blue-50 rounded-3xl border border-blue-100 flex items-start gap-4">
                    <ShieldCheck className="h-6 w-6 text-blue-600 shrink-0 mt-1" />
                    <p className="text-[10px] text-blue-700 font-medium leading-relaxed">
                       Your payout details are encrypted and stored securely. Payouts will be processed to this account only. Any change requires OTP verification.
                    </p>
                 </div>

                 <button 
                   onClick={handleSaveBankDetails}
                   className="w-full py-6 rounded-3xl bg-blue-600 hover:bg-blue-700 text-white font-black text-lg shadow-2xl shadow-blue-100 active:scale-95 transition-all"
                 >
                    Save & Verify Payout Method
                 </button>
              </div>
           </div>
        </div>
      )}
    </div>
  );
};

export default AdminFinances;
